import React from "react";
import { Lock, RefreshCw, Info, X } from "lucide-react";

interface VpnActionBlockedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenDetails?: () => void;
  onRecheck?: () => Promise<any>;
  rechecking?: boolean;
  actionTitle?: string;
}

export const VpnActionBlockedModal: React.FC<VpnActionBlockedModalProps> = ({
  isOpen,
  onClose,
  onOpenDetails,
  onRecheck,
  rechecking = false,
  actionTitle,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vpn-blocked-title"
    >
      <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-6 shadow-2xl dark:border-red-900/60 dark:bg-slate-900 animate-in zoom-in-95 duration-200">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-4 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-400">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <h2 id="vpn-blocked-title" className="text-base font-bold text-slate-900 dark:text-white">
                Action Blocked: VPN Detected
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Write operations are disabled in Read-Only mode
              </p>
              {actionTitle && (
                <p className="mt-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                  Target action: {actionTitle}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
            You cannot save changes, update profile/timezone preferences, or trigger outbound calls while connected to a VPN or proxy tunnel.
          </p>

          <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <span className="font-semibold">How to proceed:</span>
            <ol className="mt-1 list-decimal pl-4 space-y-1 text-[11px]">
              <li>Disconnect from your VPN software or proxy extension.</li>
              <li>Click <strong>Re-check Connection</strong> below to re-verify your network.</li>
              <li>If you require a corporate VPN exemption, contact your TechLadder Administrator.</li>
            </ol>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-2.5 border-t border-slate-100 pt-4 dark:border-slate-800">
          <div>
            {onOpenDetails && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenDetails();
                }}
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
              >
                <Info className="h-3.5 w-3.5" />
                <span>View Diagnostics</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {onRecheck && (
              <button
                type="button"
                disabled={rechecking}
                onClick={onRecheck}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${rechecking ? "animate-spin" : ""}`} />
                <span>{rechecking ? "Checking..." : "Re-check"}</span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VpnActionBlockedModal;
